import { DatePickerClean, IPickerProps } from "@components/inputs/date/DatePicker";
import { ICustomReferenceElement } from "@components/inputs/date/DatePickerBase";
import YearPopup from "@components/inputs/date/popup/YearPopup";
import { isValidDate } from "@components/inputs/date/utils";
import { IInputProps } from "@components/inputs/input";
import { composeRefHandlers, isDefined } from "@utils/general";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { IconSize } from "../../../enums";
import { KeyName } from "../../../keyName";
import TestIds from "../../../testIds";
import { getUtcDayjs } from "../../../types/Date";
import {
    CaretIconLeft,
    CaretIconRight,
    MonthAndYearValue,
    MonthNavButton,
    StyledMonthYearSelector,
    TableCaretIconLeft,
    TableCaretIconRight
} from "./MonthYearSelector.styles";

interface IProps extends IPickerProps, WithTranslation {
    isMain?: boolean;
    isCompact?: boolean;
}

/** Shows month and a year, without input. MonthYearPopup can be opened by clicking on the label. */
class YearSelector extends DatePickerClean<IProps> {
    static defaultProps: Partial<IPickerProps> = {
        openOnEnter: true
    };

    get pickerPopup() {
        return YearPopup;
    }

    get inputComponent(): React.ComponentType<IInputProps<Date>> {
        return null;
    }

    get popoverOffsetX(): number {
        // center the popup
        return -63;
    }

    get popoverOffsetY(): number {
        return 10;
    }

    handlePrevious = (): void => {
        const newDate = getUtcDayjs(this.props.value).subtract(1, "year");

        if (isDefined(this.props.minDate) && newDate.isBefore(this.props.minDate, "year")) {
            return;
        }

        this.props.onChange({
            value: newDate.toDate(),
            triggerAdditionalTasks: true
        });
    };

    handleNext = (): void => {
        const newDate = getUtcDayjs(this.props.value).add(1, "year");

        if (isDefined(this.props.maxDate) && newDate.isAfter(this.props.maxDate, "year")) {
            return;
        }

        this.props.onChange({
            value: newDate.toDate(),
            triggerAdditionalTasks: true
        });
    };

    handleKeyDown = (onKeyDown: (args: any) => void, e: React.KeyboardEvent): void => {
        if (e.key === KeyName.ArrowLeft) {
            this.handlePrevious();
        } else if (e.key === KeyName.ArrowRight) {
            this.handleNext();
        }
        onKeyDown(e);
    };

    getCustomReferenceElement = (args: ICustomReferenceElement): React.ReactNode => {
        return this.renderValue(args);
    };

    renderValue = (args: ICustomReferenceElement): React.ReactElement => {
        const IconLeft = this.props.isMain ? TableCaretIconLeft : CaretIconLeft;
        const IconRight = this.props.isMain ? TableCaretIconRight : CaretIconRight;

        return (
                <StyledMonthYearSelector tabIndex={0}
                                         data-testid={TestIds.YearSwitcher}
                                         ref={composeRefHandlers(args.ref, args.openerRef)}
                                         onKeyDown={this.handleKeyDown.bind(this, args.onKeyDown)}
                                         $isCompact={this.props.isCompact}
                                         $isOnlyYearPicker={true}
                                         $isMain={this.props.isMain}>
                    <MonthNavButton title={this.props.t("Components:Calendar.PreviousYear")}
                                    onClick={this.handlePrevious}
                                    tabIndex={-1}
                                    isDisabled={this.props.minDate && getUtcDayjs(this.props.value).isSame(this.props.minDate, "year")}
                                    isDecorative>
                        <IconLeft width={IconSize.M}/>
                    </MonthNavButton>
                    <MonthAndYearValue onClick={args.onClick}>
                        {isValidDate(this.props.value) && getUtcDayjs(this.props.value).get("year")}
                    </MonthAndYearValue>
                    <MonthNavButton title={this.props.t("Components:Calendar.NextYear")}
                                    onClick={this.handleNext}
                                    tabIndex={-1}
                                    isDisabled={this.props.maxDate && getUtcDayjs(this.props.value).isSame(this.props.maxDate, "year")}
                                    isDecorative>
                        <IconRight width={IconSize.M}/>
                    </MonthNavButton>
                </StyledMonthYearSelector>
        );
    };
}

export default withTranslation(["Common", "Component"])(YearSelector);